import { Component, ErrorInfo, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
  resetKey?: string;
  onReset?: () => void;
  pathname?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  eventId: string | null;
}

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, error: null, eventId: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, eventId: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[RouteErrorBoundary] Caught render error:", error, info?.componentStack);
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: { componentStack: info?.componentStack ?? "" },
      },
      tags: {
        boundary: "route",
        path: this.props.pathname ?? "unknown",
      },
    });
    this.setState({ eventId });
  }

  componentDidUpdate(prev: Props) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, eventId: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const message = this.state.error?.message || "An unexpected error occurred.";
    const eventId = this.state.eventId;
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg shadow-sm p-6 text-center">
          <h2 className="text-lg font-semibold text-[#0F2848] mb-2">This page hit a snag</h2>
          <p className="text-sm text-slate-600 mb-4">
            We caught an error before the page went blank. You can reload, or head back and try again.
          </p>
          <pre className="text-[11px] text-left text-slate-500 bg-slate-50 border border-slate-100 rounded p-2 mb-3 overflow-auto max-h-32">
            {message}
          </pre>
          {eventId && (
            <p className="text-[11px] text-slate-400 mb-4">
              Reference: <span className="font-mono">{eventId}</span>
            </p>
          )}
          <div className="flex gap-2 justify-center flex-wrap">
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, eventId: null });
                this.props.onReset?.();
              }}
              className="px-3 py-1.5 text-sm rounded-md bg-[#0F2848] text-white hover:bg-[#0F2848]/90"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Reload page
            </button>
            {eventId && (
              <button
                onClick={() => Sentry.showReportDialog({ eventId })}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Report feedback
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <ErrorBoundaryInner
      resetKey={location.pathname}
      pathname={location.pathname}
      onReset={() => navigate(0)}
    >
      {children}
    </ErrorBoundaryInner>
  );
};

export default RouteErrorBoundary;
